import { IContext } from "@src/context";
import { vhmarea, vhmproperty } from "@src/proto/jsonapi";
import { IProperty } from "../types";
import searchDataHelper, { IRCity } from "../../SearchPopup/searchDataHelper";
import singleUrlHelper, { jsonApiPathWithLocale } from "@src/utils/url";

const baseUrl = document.body.getAttribute("data-site-domain") || "";

function fetchPropertiesJson({
  locale,
}: Pick<IContext, "locale">): Promise<vhmproperty.IListProperty[]> {
  const url = `${baseUrl}${jsonApiPathWithLocale("properties", locale)}`;

  // FIXME: extract common json fetcher
  return fetch(url, {
    headers: { Accept: "application/json" },
  })
    .then((response) => {
      if (response.status != 200) {
        throw new Error(
          `Failed to fetch properties json, status: ${response.status} on url ${url}`
        );
      }
      return response.json();
    })
    .then(
      ({ properties }: { properties: vhmproperty.IListProperty[] }) =>
        properties
    );
}

function fetchParkingSpotsJson({
  locale,
}: Pick<IContext, "locale">): Promise<vhmproperty.IListProperty[]> {
  const url = `${baseUrl}${jsonApiPathWithLocale("parkingspots", locale)}`;

  // FIXME: extract common json fetcher
  return fetch(url, {
    headers: { Accept: "application/json" },
  })
    .then((response) => {
      if (response.status != 200) {
        throw new Error(
          `Failed to fetch parking spots json, status: ${response.status} on url ${url}`
        );
      }
      return response.json();
    })
    .then(
      ({ properties }: { properties: vhmproperty.IListProperty[] }) =>
        properties
    );
}

function fetchAreaJson({
  locale,
}: Pick<IContext, "locale">): Promise<vhmarea.IArea> {
  const url = `${baseUrl}${jsonApiPathWithLocale("area", locale)}`;

  // FIXME: extract common json fetcher
  return fetch(url, {
    headers: { Accept: "application/json" },
  })
    .then((response) => {
      if (response.status != 200) {
        throw new Error(
          `Failed to fetch area json, status: ${response.status} on url ${url}`
        );
      }
      return response.json();
    })
    .then((area: vhmarea.IArea) => area);
}

function fetchAllProperties(
  context: Pick<IContext, "locale" | "errorNotifier">
): Promise<IProperty[]> {
  return Promise.all([
    fetchPropertiesJson(context),
    fetchAreaJson(context),
  ]).then(([originProperties, areaJson]) => {
    const cIdRCityMap =
      searchDataHelper.pureConvertAreaResp(areaJson).cIdRCityMap;

    return originProperties.map(
      (property): IProperty => constructProperty(property, cIdRCityMap, context)
    );
  });
}

function fetchAllParkingSpots(
  context: Pick<IContext, "locale" | "errorNotifier">
): Promise<IProperty[]> {
  return Promise.all([
    fetchParkingSpotsJson(context),
    fetchAreaJson(context),
  ]).then(([originProperties, areaJson]) => {
    const cIdRCityMap =
      searchDataHelper.pureConvertAreaResp(areaJson).cIdRCityMap;

    return originProperties.map(
      (property): IProperty =>
        constructProperty(property, cIdRCityMap, context, "parking")
    );
  });
}

function constructProperty(
  property: vhmproperty.IListProperty,
  irCityMap: PlainObject<IRCity>,
  { locale, errorNotifier }: Pick<IContext, "locale" | "errorNotifier">,
  category?: string
): IProperty {
  const cityId = String(property.cid);
  const irCity = searchDataHelper.getCityWithinParent(cityId, irCityMap);
  const basePath =
    category === "parking"
      ? singleUrlHelper.getParkingPrefix(locale)
      : singleUrlHelper.getPDPPrefix(locale);
  let detailLink: string | null;

  if (irCity == null) {
    detailLink = null;
    errorNotifier.notify({
      msg: `fail to construct pdp link for the property from the area json, propertyId: ${property.id}`,
      severity: "warn",
      context: "search map",
    });
  } else {
    detailLink = `${basePath}${irCity.url}/${property.path}`;
  }

  return {
    ...property,
    detailLink,
    lat: Number(property.lat),
    lng: Number(property.lng),
  };
}

export {
  fetchAreaJson,
  fetchPropertiesJson,
  fetchParkingSpotsJson,
  fetchAllProperties,
  fetchAllParkingSpots,
};
export default fetchAllProperties;
